import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiService, MonthlyEntry } from '../services/api'
import { useOverlay } from '../context/OverlayContext'
import { money, Avatar, Spinner } from '../lib/ui'

const signedRemaining = (m: MonthlyEntry) =>
  m.who_owes === 'Timna' ? m.remaining_balance : m.who_owes === 'David' ? -m.remaining_balance : 0

const HomePage: React.FC = () => {
  const { openMonth } = useOverlay()
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear())

  // One request for every month across all years. The backend returns the
  // whole history in a single pass, so the Balance page no longer fans out one
  // request per year (which serialized on the single worker). Year switching is
  // then instant — it just refilters data already in hand.
  const { data: monthlyData, isLoading: loading } = useQuery({
    queryKey: ['monthly-report', 'all'],
    queryFn: () => apiService.getMonthlyReport(),
  })

  const allMonths: MonthlyEntry[] = (monthlyData || [])
    .slice()
    .sort((a, b) => b.month_id.localeCompare(a.month_id))

  // Years that actually have receipts, newest first.
  const years = allMonths.length
    ? Array.from(new Set(allMonths.map((m) => parseInt(m.month_id.slice(0, 4))))).sort((a, b) => b - a)
    : [new Date().getFullYear()]

  const activeYear = years.includes(selectedYear) ? selectedYear : years[0]
  const yearMonths = allMonths.filter((m) => parseInt(m.month_id.slice(0, 4)) === activeYear)

  const heroLoading = loading
  const monthsLoading = loading

  // All-time standing (net of payments). net > 0 => Timna owes David.
  const net = allMonths.reduce((s, m) => s + signedRemaining(m), 0)
  const allSettled = Math.abs(net) < 0.01
  const creditor = net > 0 ? 'David' : 'Timna'
  const ower = net > 0 ? 'Timna' : 'David'
  const recent = allMonths.find((m) => m.who_owes !== 'Nobody') || allMonths[0]

  // Selected-year context
  const yDavid = yearMonths.reduce((s, m) => s + m.david_paid, 0)
  const yTimna = yearMonths.reduce((s, m) => s + m.timna_paid, 0)
  const yTotal = yDavid + yTimna
  const yNet = yearMonths.reduce((s, m) => s + signedRemaining(m), 0)
  const ySettled = yearMonths.filter((m) => m.who_owes === 'Nobody').length

  return (
    <>
      <div className="hero glass">
        <div className="eyebrow">Current standing · all time</div>
        {heroLoading ? (
          <div className="center-pad"><Spinner /></div>
        ) : allSettled ? (
          <>
            <div className="amount">All settled</div>
            <div className="who">Nobody owes anybody right now</div>
          </>
        ) : (
          <>
            <div className="amount">{money(Math.abs(net))}</div>
            <div className="who"><b>{ower}</b> owes <b>{creditor}</b> overall</div>
            {recent && <button className="cta" onClick={() => openMonth(recent.month_id)}>Record payment</button>}
          </>
        )}
      </div>

      <div className="yearbar">
        {years.map((y) => (
          <button key={y} className={`ychip${y === activeYear ? ' on' : ''}`} onClick={() => setSelectedYear(y)}>{y}</button>
        ))}
      </div>

      <div className="ysum glass">
        <div><span className="k">{activeYear} spent</span><span className="v">{money(yTotal)}</span></div>
        <div>
          <span className="k">Year balance</span>
          <span className="v">
            {Math.abs(yNet) < 0.01
              ? <span style={{ color: 'var(--david)' }}>Settled</span>
              : <span><b>{yNet > 0 ? 'Timna' : 'David'}</b> owes {money(Math.abs(yNet))}</span>}
          </span>
        </div>
        <div><span className="k">Settled</span><span className="v">{ySettled}/{yearMonths.length} months</span></div>
      </div>

      <div className="split-row">
        <div className="who-card glass">
          <Avatar who="David" />
          <div className="tiny muted">David paid · {activeYear}</div>
          <div className="val" style={{ color: 'var(--david)' }}>{money(yDavid)}</div>
        </div>
        <div className="who-card glass">
          <Avatar who="Timna" />
          <div className="tiny muted">Timna paid · {activeYear}</div>
          <div className="val" style={{ color: 'var(--timna)' }}>{money(yTimna)}</div>
        </div>
      </div>

      <div className="section-title">
        Months · {activeYear} <span className="tiny muted" style={{ fontWeight: 400 }}>— tap for tickets &amp; payments</span>
      </div>
      <div>
        {monthsLoading ? (
          <div className="center-pad"><Spinner /></div>
        ) : yearMonths.length ? yearMonths.map((m) => {
          const settled = m.who_owes === 'Nobody'
          return (
            <div key={m.month_id} className="row glass" onClick={() => openMonth(m.month_id)}>
              <div className="row-left">
                <div className="ico" style={{ fontSize: 12, fontWeight: 700 }}>{m.month_name.slice(0, 3)}</div>
                <div>
                  <div className="row-title">{m.month_name}</div>
                  <div className="row-sub">
                    {money(m.david_paid + m.timna_paid)} spent · {m.payments.length} payment{m.payments.length !== 1 ? 's' : ''}
                  </div>
                </div>
              </div>
              <div className="row-right">
                {settled
                  ? <span className="pill pill-settled">All settled</span>
                  : <span className="owe">{m.who_owes} · {money(m.remaining_balance)}</span>}
                <span className="tiny muted">view →</span>
              </div>
            </div>
          )
        }) : <div className="note note-info">No receipts in {activeYear}.</div>}
      </div>
    </>
  )
}

export default HomePage
